import React, { ReactNode, useCallback, useEffect } from 'react';
import { Box, Button, ButtonProps, Chip, DialogActions,
  DialogTitle, IconButton, Popper, Stack,
  SxProps,
  Theme,
  TooltipProps, Typography } from '@mui/material';
import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';
import {  Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import arrowDown from '../../assets/tooltipArrows/down.svg';
import arrowLeft from '../../assets/tooltipArrows/left.svg';
import arrowRight from '../../assets/tooltipArrows/right.svg';
import arrowUp from '../../assets/tooltipArrows/up.svg';
import useScreenSize from '../../utils/useScreenSize';
import createNonPropagatingEventHandler from '../../utils/createNonPropagatingEventHandler';
import { shouldDisplayHintsAndOnboardingDialogs } from '../../utils/shouldDisplayHintsAndOnboardingDialogs';

export interface HintAction {
  label: string;
  onClick: () => void;
  buttonProps?: ButtonProps;
  withoutDismiss?: boolean;
}

export interface HintProps  {
  placement?: TooltipProps['placement'];
  isNew?: boolean;
  tabletPlacement?: TooltipProps['placement'];
  mobilePlacement?: TooltipProps['placement'];
  mobileMaxWidth?: number;
  title?: string;
  description?: string | ReactNode;
  htmlDescription?: ReactNode;
  offset?: { x?: number; y?: number };
  actions?: {
    primary?: HintAction;
    secondary?: HintAction;
  }
  maxWidth?: number;
  width?: number,
  mobileWidth?: number
  disablePortal?: boolean;
  open: boolean;
  children: React.ReactElement<any, any>;
  onClose: () => void;
  bottomLeftText?: string;
  useDesktopButtonDesign?: boolean;
  sx?: SxProps<Theme>;
  preventOverflow?: boolean;
}

// As this component needs a ref-able children, keep in mind as our components are mostly FC.
// Use ref-able components like Box, div etc. when you see the tooltip doesn't appear.
export const Hint: React.FC<HintProps> = ({ 
  title, 
  placement = 'bottom', 
  open, 
  maxWidth, 
  mobileMaxWidth,
  width,
  mobileWidth,
  onClose, 
  mobilePlacement, 
  children, 
  disablePortal = true,
  description,
  htmlDescription,
  actions,
  isNew,
  bottomLeftText,
  useDesktopButtonDesign,
  tabletPlacement,
  sx,
  offset,
  preventOverflow = false,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const childrenRef = React.useRef(null);
  const { isXsScreen, isSmScreen, isMdScreen, isLgScreen } = useScreenSize();
  const isDesktopScreen = isLgScreen || isMdScreen;
  
  useEffect(() => {
    setAnchorEl(childrenRef.current);
  }, [childrenRef]);

  const getPlacement = useCallback(() => {
    if (isXsScreen && mobilePlacement) {
      return mobilePlacement;
    } else if (isSmScreen && tabletPlacement) {
      return tabletPlacement;
    } else {
      return placement;
    }
  }, [mobilePlacement, placement, isXsScreen]);

  const responsivePlacement = getPlacement();

  const iconButton = <IconButton
    data-testid='hint-close'
    sx={{
      color: palette.primary.contrastText,
      stroke: 'transparent',
      clear: 'right',
    }}
    size='small'
    aria-label='close'
    onClick={createNonPropagatingEventHandler(onClose)}>
    <Close />
  </IconButton>;

  const getButtons = () => {
    if (actions) {
      return <>{actions.secondary && <Button
        size='small'
        sx={{
          borderColor: palette.primary.contrastText,
          color: palette.primary.contrastText,
          '&:hover': {
            backgroundColor: palette.primary.hover,
          },
        }}
        color='inherit'
        variant='outlined'
        onClick={actions.secondary.onClick}
        {...actions.secondary.buttonProps}
      >
        {actions.secondary.label}
      </Button>}
      {actions.primary &&
      <Button
        size='small'
        color='inherit'
        variant='contained'
        onClick={actions.primary.onClick}
        {...actions.primary.buttonProps}
      >
        {actions.primary.label}
      </Button>
      }</>;
    }
    return;
  };

  const bubble = <Box>
    <Box id='bubble' sx={{
      textAlign: 'left',
      display: 'flex',
      minWidth: 240,
      maxWidth: (isXsScreen ? mobileMaxWidth : maxWidth) || 400,
      width: (isXsScreen ? mobileWidth : width) || undefined,
      flexDirection: 'column',
      alignItems: 'flex-start',
      borderRadius: borderRadius.md,
      backgroundColor: palette.primary.main,
      position: 'relative',
      paddingY: spaces.md,
      ...sx,
    }}>
      {(htmlDescription || description || title) && <DialogTitle sx={{
        paddingY: 0,
        paddingX: spaces.md,
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        gap: spaces.xs,
      }}>
        <Box sx={{ paddingY: spaces.xxs, display: 'flex', flexDirection: 'column', gap: spaces.xs, flexGrow: 1 }}>
          {
            title && 
            <Box sx={{ display: 'flex', padding: 0, alignItems: 'flex-start', gap: spaces.sm, alignSelf: 'stretch' }}>
              {isNew && <Chip label={t('New')} color='info' size='small' sx={{ paddingX: spaces.xs }} />}
              <Typography variant='h6' color={palette.primary.contrastText}>
                {title}
              </Typography>
            </Box>
          }
          {
            description &&
              <Typography
                variant='body2'
                sx={{
                  color: palette.primary.contrastText,
                  '& a': {
                    color: 'inherit',
                    textDecoration: 'none',
                  },
                }}
              >
                {description}
              </Typography>
          }
          {htmlDescription}
        </Box>
        {iconButton}
      </DialogTitle>}
      {actions && Object.keys(actions).length !== 0 &&
      (
        (isXsScreen && !useDesktopButtonDesign) ? 
          <Stack
            spacing={1}
            direction='column'
            sx={{
              padding: spaces.md,
              paddingBottom: 0,
              alignSelf: 'stretch',
            }}
          >
            {getButtons()}
          </Stack> :
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            padding: spaces.md,
            paddingBottom: 0,
            alignItems: 'center',
          }}>
            {bottomLeftText && <Typography
              variant='body1'
              sx={{  color: palette.primary.contrastText }}
            >
              {bottomLeftText}
            </Typography>}
            <DialogActions sx={{
              display: 'flex',
              flex: '1 0 auto',
              padding: 0,
              ...((isDesktopScreen || useDesktopButtonDesign) && {
                justifyContent: 'flex-end',
              }),
              ...((isDesktopScreen && !useDesktopButtonDesign) && {
                justifyContent: 'space-between',
                '& > button': {
                  flexGrow: 1,
                  flexBasis: 0,
                }
              }),
            }}>
              {getButtons()}
            </DialogActions>
          </Box>
      )
      }
    </Box>
  </Box>;

  const arrowOffset = 8;

  const getSvgIcon = (placement: HintProps['placement']) => {
    let icon;
    let arrowStyleProperties;

    switch (placement) {
      case 'top':
      case 'top-start':
      case 'top-end':
        icon = arrowDown;
        arrowStyleProperties = { bottom: -arrowOffset, };
        break;
      case 'bottom':
      case 'bottom-start':
      case 'bottom-end':
        icon = arrowUp;
        arrowStyleProperties = {  top: -arrowOffset, };
        break;
      case 'left':
      case 'left-start':
      case 'left-end':
        icon = arrowRight;
        arrowStyleProperties = { right: -arrowOffset };
        break;
      case 'right':
      case 'right-start':
      case 'right-end':
        icon = arrowLeft;
        arrowStyleProperties = { left: -arrowOffset };
        break;
      default:
        throw new Error('Invalid placement');
    }

    return (
      <Box 
        data-popper-arrow 
        id='arrow' 
        sx={{
          lineHeight: 0,
          color: palette.primary.main,
          ...arrowStyleProperties,
        }}>
        <Box sx={{
          position: 'relative',
          '& > svg': {
            width: 'unset',
            height: 'unset',
          }
        }}>
          {React.createElement(icon)}
        </Box>
      </Box>
    );
  };

  const getOffsetBasedOnPlacement = (placement: HintProps['placement']) => {
    let newOffset = [(offset?.y || 0), (offset?.x || 0) + arrowOffset + 4];
    const offsetX = 6;

    if (placement === 'bottom-end' || placement === 'top-end') {
      newOffset = [(offset?.y || 0) + offsetX, (offset?.x || 0) + arrowOffset + 4];
    } else if (placement === 'bottom-start' || placement === 'top-start') {
      newOffset = [(offset?.y || 0) - offsetX, (offset?.x || 0) + arrowOffset + 4];
    }

    return newOffset;
  };

  return <>
    {React.cloneElement(React.Children.only(children), { ref: childrenRef, handleHintClose: onClose })}
    {anchorEl && <Popper
      open={open && shouldDisplayHintsAndOnboardingDialogs()}
      anchorEl={anchorEl}
      placement={responsivePlacement} 
      disablePortal={disablePortal}
      modifiers={[
        {
          name: 'arrow',
          enabled: true,
          options: {
            padding: spaces.lg,
          },
        },
        {
          name: 'offset',
          options: {
            offset: getOffsetBasedOnPlacement(responsivePlacement),
          }
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: [],
          },
        },
        {
          name: 'preventOverflow',
          enabled: preventOverflow,
        },
      ]}
      {...(preventOverflow && {
        sx: {
          zIndex: '1400!important', // Note: zIndex higher that overflow behind side navbar
        }
      })}
    >
      {getSvgIcon(responsivePlacement)}
      {bubble}
    </Popper>}
  </>;
};

export default Hint;
